import { companyStrings } from '@data/company';
import { userStrings } from '@data/user';
import { PaginationType } from '@models/next/PaginationType';
import { f } from '@utils/nextForms';
import { z } from 'zod';

export const UserFields = z.object({
  id: z.number(),
  email: z.string().label(userStrings.email),
  last_name: z.string().label(userStrings.last_name),
  first_name: z.string().label(userStrings.first_name),
  role: z.string(),
  cellphone: z.string().label(userStrings.cellphone),
  last_login: z.string().nullish(),
  landline: z.string(),
  sns_id: z.string(),
  email_verified: z.boolean(),
});

export type UserFields = z.infer<typeof UserFields>;

export const User = z.object({
  id: UserFields.shape.id,
  email: UserFields.shape.email,
  last_name: UserFields.shape.last_name,
  first_name: UserFields.shape.first_name,
  role: UserFields.shape.role,
  cellphone: UserFields.shape.cellphone,
  last_login: UserFields.shape.last_login,
  landline: UserFields.shape.landline,
  sns_id: UserFields.shape.sns_id,
  email_verified: UserFields.shape.email_verified,
});

export type User = z.infer<typeof User>;

export const AdminUser = UserFields.pick({
  id: true,
  email: true,
  last_name: true,
  first_name: true,
  role: true,
  cellphone: true,
  last_login: true,
  landline: true,
  sns_id: true,
  email_verified: true,
});

export type AdminUser = z.infer<typeof AdminUser>;

export const DashboardUserSearchQuery = z
  .object({
    page: PaginationType.shape.page,
    page_size: PaginationType.shape.page_size,
    company_name: z.string().label(companyStrings.company_name),
    email: UserFields.shape.email,
    fullname: z.string().label(companyStrings.principal_name),
    ordering: z.string().array(),
    login_status: z.string(),
    date_joined_after: f.dateString().label(companyStrings.created_at),
    date_joined_before: f.dateString().label(companyStrings.created_at),
  })
  .partial();

export type DashboardUserSearchQuery = z.TypeOf<typeof DashboardUserSearchQuery>;

export const RegistrationForm = z
  .object({
    company_name: z.string().min(1).label(userStrings.company_name),
    last_name: UserFields.shape.last_name.min(1),
    first_name: UserFields.shape.first_name.min(1),
    cellphone: f.withPhoneRegex(UserFields.shape.cellphone),
    landline: UserFields.shape.landline,
    email: UserFields.shape.email.email(),
    password1: f.secret().min(8).max(16).label(userStrings.password1),
    password2: f.secret().min(8).max(16).label(userStrings.password2),
    agree_to_privacy_policy: z.boolean().refine((v) => !!v),
  })
  .superRefine(({ password1, password2 }, ctx) => {
    if (password1 && password2 && password1 !== password2) {
      ctx.addIssue({
        code: 'custom',
        path: ['password2'],
        message: userStrings.notSamePassword2,
      });
    }
  });
